import type { DeltaKind, Polarity } from "./report-delta";
import { count, num } from "./report-figures";
import type { FigureFormat } from "./report-format";
import type { Figures, ReportName } from "./report-types";

export const KPI_IDS = ["created", "backlog", "frt", "ttr", "slaFirst", "slaRes", "csat", "reopen", "deflection", "intake"] as const;
export type KpiId = (typeof KPI_IDS)[number];

/** One headline tile: which report feeds it, how its number is read, shown and compared (C16 mockup, "i" copy under `service.reports.def.*`). */
export interface KpiSpec {
  id: KpiId;
  report: ReportName;
  format: FigureFormat;
  delta: DeltaKind;
  polarity: Polarity;
  /** The figure of a period (`totals`, or `previous_totals` for the comparison). */
  pick: (figures: Figures | null | undefined) => number | null;
}

export const KPI_SPECS: Readonly<Record<KpiId, KpiSpec>> = {
  created: { id: "created", report: "volume", format: "count", delta: "relative", polarity: "neutral", pick: (f) => num(f, "created") },
  backlog: { id: "backlog", report: "backlog", format: "count", delta: "absolute", polarity: "lower-better", pick: (f) => num(f, "total") },
  frt: { id: "frt", report: "first_response", format: "minutes", delta: "absolute", polarity: "lower-better", pick: (f) => num(f, "median_minutes") },
  ttr: { id: "ttr", report: "resolution", format: "minutes", delta: "absolute", polarity: "lower-better", pick: (f) => num(f, "median_minutes") },
  slaFirst: { id: "slaFirst", report: "sla", format: "percent", delta: "points", polarity: "higher-better", pick: (f) => num(f, "metrics", "first_response", "compliance") },
  slaRes: { id: "slaRes", report: "sla", format: "percent", delta: "points", polarity: "higher-better", pick: (f) => num(f, "metrics", "resolution", "compliance") },
  csat: { id: "csat", report: "csat", format: "score", delta: "absolute", polarity: "higher-better", pick: (f) => num(f, "mean_score") },
  reopen: { id: "reopen", report: "reopen", format: "percent", delta: "points", polarity: "lower-better", pick: (f) => num(f, "rate") },
  deflection: { id: "deflection", report: "deflection", format: "percent", delta: "points", polarity: "higher-better", pick: (f) => num(f, "rate") },
  intake: { id: "intake", report: "intake", format: "percent", delta: "points", polarity: "higher-better", pick: (f) => num(f, "rate") },
};

export const OVERVIEW_KPIS: readonly KpiId[] = ["created", "backlog", "frt", "ttr", "slaFirst", "slaRes", "csat", "reopen"];
export const SLA_KPIS: readonly KpiId[] = ["slaFirst", "slaRes", "frt", "ttr"];
export const CSAT_KPIS: readonly KpiId[] = ["csat", "reopen"];
export const DEFLECTION_KPIS: readonly KpiId[] = ["deflection", "intake"];

/** The reports a set of tiles reads, each once. */
export function reportsOf(ids: readonly KpiId[]): ReportName[] {
  return [...new Set(ids.map((id) => KPI_SPECS[id].report))];
}

/** Deflection counts only the conversations whose 24-hour window has closed. */
export function deflectionEligible(totals: Figures | null | undefined): number {
  return count(totals, "started") - count(totals, "open");
}
